import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Box,
    TextField,
    IconButton,
    Divider,
} from '@mui/material';
import { MdClose, MdLock, MdCheckCircle } from 'react-icons/md';

interface ModalAtivacaoProps {
    open: boolean;
    onClose: () => void;
    onAtivar: (senha: string) => void;
    limitePlacas: number;
}

function ModalAtivacao({ open, onClose, onAtivar, limitePlacas }: ModalAtivacaoProps) {
    const [senha, setSenha] = useState('');

    const precoBase = 19.90;
    const placasInclusas = 5;
    const precoPorPlacaAdicional = 3.00;

    const placasAdicionais = Math.max(0, limitePlacas - placasInclusas);
    const valorAdicional = placasAdicionais * precoPorPlacaAdicional;
    const valorTotal = precoBase + valorAdicional;

    const handleAtivar = () => {
        if (senha.trim()) {
            onAtivar(senha);
            setSenha('');
        }
    };

    const handleClose = () => {
        setSenha('');
        onClose();
    };

    const formatarValor = (valor: number) => valor.toFixed(2).replace('.', ',');

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
            {/* Header */}
            <Box sx={{
                background: 'linear-gradient(135deg, #0a1929 0%, #132f4c 100%)',
                color: 'white',
                p: 3,
                textAlign: 'center',
                position: 'relative',
            }}>
                <IconButton onClick={handleClose} size="small" sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.7)' }}>
                    <MdClose size={20} />
                </IconButton>
                <Typography variant="h5" fontWeight="800">
                    Ativar LocaSmartCheck
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                    Confirme os detalhes da sua ativação
                </Typography>
            </Box>

            <DialogContent sx={{ p: 3 }}>
                {/* Resumo do plano */}
                <Box sx={{
                    border: '2px solid #d6e4f0',
                    borderRadius: 2,
                    p: 2.5,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Plano base (até {placasInclusas} placas)</Typography>
                        <Typography variant="body1" fontWeight="600">R$ {formatarValor(precoBase)}/mês</Typography>
                    </Box>

                    {limitePlacas > 0 && (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Limite de placas: {limitePlacas}
                                </Typography>
                                {placasAdicionais > 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        +{placasAdicionais} adicional(is)
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <MdCheckCircle style={{ color: '#4caf50', fontSize: 16 }} />
                                        <Typography variant="body2" sx={{ color: '#4caf50' }}>Incluso</Typography>
                                    </Box>
                                )}
                            </Box>

                            {placasAdicionais > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Excedente ({placasAdicionais} x R$ {formatarValor(precoPorPlacaAdicional)})
                                    </Typography>
                                    <Typography variant="body1" fontWeight="600">
                                        R$ {formatarValor(valorAdicional)}/mês
                                    </Typography>
                                </Box>
                            )}

                            <Divider sx={{ my: 1.5 }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body1" fontWeight="700" sx={{ color: '#132f4c' }}>
                                    Total mensal
                                </Typography>
                                <Typography variant="h5" fontWeight="800" sx={{ color: '#132f4c' }}>
                                    R$ {formatarValor(valorTotal)}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>

                {/* Info de cobranca */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1.5,
                    backgroundColor: '#fff3e0',
                    border: '1px solid #ffe0b2',
                    mb: 3,
                }}>
                    <Typography variant="body2" sx={{ color: '#e65100' }}>
                        Este valor será acrescido na sua assinatura atual do LocaSmartPro. Para planos não-mensais, será gerada uma cobrança proporcional aos dias restantes do ciclo atual.
                    </Typography>
                </Box>

                {/* Senha */}
                <TextField
                    fullWidth
                    type="password"
                    label="Digite sua senha para confirmar"
                    placeholder="Sua senha do LocaSmartPro"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAtivar(); }}
                    InputProps={{
                        startAdornment: <MdLock style={{ color: '#9e9e9e', marginRight: 8, fontSize: 20 }} />,
                    }}
                    sx={{ mb: 3 }}
                />

                {/* Botao CTA */}
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleAtivar}
                    disabled={!senha.trim()}
                    sx={{
                        py: 1.5,
                        fontSize: 16,
                        fontWeight: 700,
                        borderRadius: 2,
                        background: senha.trim() ? 'linear-gradient(135deg, #0a1929 0%, #1a3a5c 100%)' : undefined,
                        boxShadow: senha.trim() ? '0 4px 15px rgba(10, 25, 41, 0.4)' : undefined,
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(10, 25, 41, 0.6)',
                        },
                    }}
                >
                    Ativar LocaSmartCheck
                </Button>

                <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                    <Button variant="text" size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
                        Cancelar
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default ModalAtivacao;
